"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { Briefcase, Calendar, Check, FileText, Mail } from "lucide-react";

import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { useLanguage } from "../i18n/LanguageProvider";
import { ambilTeks, kodeBahasa } from "../data/pengumuman";
import { LOWONGAN, KONTAK_HRD, masihDibuka, tautanLamar } from "./data";

export default function KarirContent() {
  const ctx = useLanguage();
  const kode = kodeBahasa(ctx);

  /* Mulai dari SEMUA lowongan, disaring setelah menempel.

     Halaman ini di-render statis saat build. Kalau disaring saat
     render, "hari ini" versi peladen adalah tanggal build - bisa
     berminggu-minggu lalu - dan hasilnya berbeda dari saringan di
     browser: hydration error. Menyaring setelah menempel memakai
     tanggal pengunjung yang sebenarnya. Lowongan yang sudah tutup
     sempat terlihat sekejap; itu harga yang wajar. */
  const [daftar, setDaftar] = useState(LOWONGAN);
  useEffect(() => {
    const sekarang = new Date();
    setDaftar(LOWONGAN.filter((l) => masihDibuka(l, sekarang)));
  }, []);

  const formatTanggal = (iso) =>
    new Date(iso).toLocaleDateString(kode === "en" ? "en-GB" : "id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    });

  return (
    <div className="relative w-full overflow-x-hidden bg-[var(--c-surface)] pt-[64px] md:pt-[112px]">
      <main className="mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 md:pb-24 md:pt-16 lg:px-10">

        {/* ---------- KEPALA HALAMAN ---------- */}
        <Reveal>
          <header className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#0f6a3b] sm:text-sm">
              {ambilTeks({ id: "JMC Karir", en: "JMC Careers" }, kode)}
            </p>
            <h1 className="text-3xl font-bold leading-[1.15] tracking-tight text-[var(--c-ink)] sm:text-4xl">
              {ambilTeks(
                { id: "Bergabung bersama RS Jakarta Medical Center", en: "Join Jakarta Medical Center Hospital" },
                kode
              )}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-[var(--c-body)] sm:text-base">
              {ambilTeks(
                {
                  id: "Berikut posisi yang sedang kami buka. Lamaran dikirim melalui email dengan melampirkan berkas yang diminta pada masing-masing posisi.",
                  en: "These are the positions we are currently hiring for. Apply by email, attaching the documents listed for each position.",
                },
                kode
              )}
            </p>
          </header>
        </Reveal>

        {/* ---------- DAFTAR LOWONGAN ---------- */}
        {daftar.length === 0 ? (
          /* Keadaan kosong ditulis sungguhan, bukan dibiarkan jadi
             halaman kosong. Begitu semua `ditutup` lewat, inilah yang
             dilihat pengunjung - dan "belum ada lowongan" jauh lebih
             baik daripada halaman yang terlihat rusak. */
          <div className="mx-auto max-w-xl rounded-2xl border border-[var(--c-line)] bg-white p-8 text-center dark:bg-white/5">
            <Briefcase className="mx-auto mb-3 h-8 w-8 text-[#0f6a3b]" aria-hidden="true" />
            <p className="text-base text-[var(--c-body)]">
              {ambilTeks(
                {
                  id: "Saat ini belum ada lowongan yang dibuka. Silakan kembali lagi nanti.",
                  en: "There are no open positions right now. Please check back later.",
                },
                kode
              )}
            </p>
          </div>
        ) : (
          /* Dua kolom mulai lg. Di bawah itu satu kolom: poster persegi
             di kartu selebar setengah tablet terlalu kecil untuk
             dikenali. */
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {daftar.map((l, i) => (
              <Reveal key={l.slug} delay={i * 80}>
                {/* id = slug, supaya HRD bisa membagikan tautan langsung
                    ke satu posisi: rsjmc.id/karir#apoteker */}
                <article
                  id={l.slug}
                  className="flex h-full scroll-mt-32 flex-col overflow-hidden rounded-[28px] border border-[var(--c-line)] bg-white dark:bg-white/5"
                >
                  {/* Poster hanya dirender kalau ada. Entri yang masih
                      menunggu poster HRD (poster: null) melewati blok
                      ini, jadi kartunya tetap tampil tanpa gambar -
                      bukan error. Entri lama yang punya poster tampil
                      persis seperti sebelumnya. */}
                  {l.poster?.src && (
                    <Image
                      src={l.poster.src}
                      width={l.poster.lebar}
                      height={l.poster.tinggi}
                      alt={ambilTeks(l.poster.alt, kode)}
                      sizes="(min-width: 1024px) 600px, 100vw"
                      className="aspect-square w-full bg-[#eaf2fb] object-contain dark:bg-white/5"
                    />
                  )}

                  <div className="flex flex-1 flex-col p-6 sm:p-8">
                    <h2 className="mb-1 text-2xl font-bold tracking-tight text-[var(--c-ink)]">
                      {ambilTeks(l.posisi, kode)}
                    </h2>

                    {/* Batas waktu hanya ditulis kalau ada. Menulis
                        "Dibuka sampai: -" lebih membingungkan daripada
                        tidak menulis apa pun. */}
                    {l.ditutup && (
                      <p className="mb-4 flex items-center gap-1.5 text-sm text-[var(--c-body)]">
                        <Calendar className="h-4 w-4" aria-hidden="true" />
                        {ambilTeks({ id: "Dibuka sampai", en: "Open until" }, kode)}{" "}
                        <time dateTime={l.ditutup} className="font-semibold text-[var(--c-ink)]">
                          {formatTanggal(l.ditutup)}
                        </time>
                      </p>
                    )}

                    {/* Kalau kualifikasi sudah diisi: tampilkan heading +
                        daftar seperti biasa. Kalau masih kosong (entri
                        admin menunggu HRD): tampilkan badge "Sedang
                        Disiapkan". Badge ini hilang sendiri begitu
                        kualifikasi diisi. */}
                    {l.kualifikasi.length > 0 ? (
                      <>
                        <h3 className="mb-3 mt-4 text-sm font-semibold uppercase tracking-wide text-[#0f6a3b]">
                          {ambilTeks({ id: "Kualifikasi", en: "Qualifications" }, kode)}
                        </h3>
                        <ul className="mb-6 grid gap-2.5">
                          {l.kualifikasi.map((k, j) => (
                            <li key={j} className="flex items-start gap-2.5">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0f6a3b]" aria-hidden="true" />
                              <span className="text-sm leading-snug text-[var(--c-body)] sm:text-[15px]">
                                {ambilTeks(k, kode)}
                              </span>
                            </li>
                          ))}
                        </ul> 
                      </>
                    ) : (
                      <p className="mb-6 mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-[#0f6a3b]/20 bg-[#0f6a3b]/[0.06] px-3.5 py-1.5 text-sm font-medium text-[#0f6a3b]">
                        <Calendar className="h-4 w-4" aria-hidden="true" />
                        {ambilTeks(
                          {
                            id: "Sedang Disiapkan",
                            en: "Being prepared",
                          },
                          kode
                        )}
                      </p>
                    )}

                    {/* Kotak berkas ikut disembunyikan selama kualifikasi
                        kosong: kartu "Sedang Disiapkan" belum menuntut
                        berkas apa pun. Muncul lagi begitu kualifikasi
                        diisi - kondisinya sama dengan badge di atas. */}
                    {l.kualifikasi.length > 0 && (
                      <div className="mb-6 flex gap-3 rounded-2xl bg-[#0f6a3b]/[0.06] p-4">
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#0f6a3b]" aria-hidden="true" />
                        <p className="text-sm leading-relaxed text-[var(--c-body)]">
                          {ambilTeks(l.berkas, kode)}
                        </p>
                      </div>
                    )}

                    {/* mt-auto mendorong isi ke dasar kartu, jadi bagian
                        bawah kedua kartu sejajar walau jumlah kualifikasinya
                        berbeda. */}
                    <div className="mt-auto">
                      {/* Kartu tanpa kualifikasi ("Sedang Disiapkan") belum
                          membuka kanal lamaran: tombol + email diganti "-".
                          Keduanya kembali otomatis begitu kualifikasi diisi. */}
                      {l.kualifikasi.length > 0 ? (
                        <>
                          <a
                            href={tautanLamar(l)}
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0f6a3b] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#0c5730] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0f6a3b] sm:w-auto sm:text-base"
                          >
                            <Mail className="h-4 w-4" aria-hidden="true" />
                            {ambilTeks({ id: "Lamar via email", en: "Apply by email" }, kode)}
                          </a>

                          <p className="mt-3 text-sm text-[var(--c-body)]">
                            {ambilTeks({ id: "atau kirim ke", en: "or send to" }, kode)}{" "}
                            <span className="select-all font-semibold text-[var(--c-ink)]">
                              {KONTAK_HRD.email}
                            </span>
                          </p>
                        </>
                      ) : (
                        <p className="text-base font-semibold text-[var(--c-body)]">-</p>
                      )}
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
